import { ColumnTable, Icon } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDate } from '../../../../../../utils/dates';
import { format as formatPercentage } from '../../../../../../utils/percentage';
import { StatusBadge } from '../../shared/StatusBadge';
import { LateInterestRateItem } from '../api';

const dictPrefix = 'financialPortal.settings.lateInterest';

interface Options {
  onEdit: (item: LateInterestRateItem) => void;
  onDelete: (item: LateInterestRateItem) => void;
}

export function useRateColumns({
  onEdit,
  onDelete,
}: Options): ColumnTable<LateInterestRateItem>[] {
  const { t } = useTranslation();

  return useMemo<ColumnTable<LateInterestRateItem>[]>(
    () => [
      {
        columnName: 'monthlyRate',
        headerText: t(`${dictPrefix}.columns.monthlyRate`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) =>
          formatPercentage(row.monthlyRate, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
      },
      {
        columnName: 'dailyRate',
        headerText: t(`${dictPrefix}.columns.dailyRate`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        // Solo la vista se redondea a dos decimales; el cálculo de intereses
        // sigue usando `row.dailyRate` sin redondear.
        cellFormat: ({ row }) =>
          formatPercentage(row.dailyRate, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
      },
      {
        columnName: 'graceDays',
        headerText: t(`${dictPrefix}.columns.graceDays`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        columnName: 'base',
        headerText: t(`${dictPrefix}.columns.base`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        // null = fila anterior al rework por ítem: se muestra el default con
        // el que el motor congelaba (residual total).
        cellFormat: ({ row }) =>
          t(`${dictPrefix}.baseOptions.${row.base ?? 'totalResidual'}`),
      },
      {
        columnName: 'validFrom',
        headerText: t(`${dictPrefix}.columns.validFrom`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => formatDate(row.validFrom, 'DD/MM/YYYY'),
      },
      {
        columnName: 'isActive',
        headerText: t(`${dictPrefix}.columns.status`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => <StatusBadge isActive={row.isActive} />,
      },
      {
        columnName: 'id',
        headerText: t(`${dictPrefix}.columns.actions`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        width: 90,
        // Sin iconos en las filas que ya rigen: el historial no se reescribe.
        // El servidor lo rechaza igual, esto sólo evita ofrecerlo.
        cellFormat: ({ row }) =>
          row.isEditable ? (
            <div className="d-flex justify-content-center" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn p-0"
                aria-label={t(`${dictPrefix}.columns.edit`)}
                title={t(`${dictPrefix}.columns.edit`)}
                onClick={() => onEdit(row)}
              >
                <Icon size="15" name="edit" color="primary" />
              </button>
              <button
                type="button"
                className="btn p-0"
                aria-label={t(`${dictPrefix}.columns.delete`)}
                title={t(`${dictPrefix}.columns.delete`)}
                onClick={() => onDelete(row)}
              >
                <Icon size="15" name="trash" color="primary" />
              </button>
            </div>
          ) : null,
      },
    ],
    [t, onEdit, onDelete],
  );
}
